/**
 * A model step's prompt, put together from its asks and blocks within the
 * transport's token budget.
 */

import type { CategoryOption } from '../../categories.js';
import { CATEGORY_JSON_SCHEMA, compactCategorySchema } from '../../extraction.js';
import { buildCategorySystemPrompt, buildCategoryUserPrompt, buildCompactCategorySystemPrompt, type CategoryLine, type CorrectionContext } from '../../prompt.js';
import { estimateInputTokens } from '../../providers/budget.js';
import type { ProviderImage } from '../../providers/core.js';
import type { CompanyInfo, OcrLine } from '../types.js';
import { ASKS, schemaFor, type AskName } from './asks.js';
import { BLOCKS, companyText, type BlockContext, type BlockName } from './blocks.js';
import { baseSystemPrompt, baseUserPrompt } from './instructions.js';

export interface ReadPromptInput {
  blocks: readonly BlockName[];
  asks: readonly AskName[];
  image: ProviderImage | null;
  text: string | null;
  lines: readonly string[];
  /** The lines with the OCR engine's detail, for blocks that use it. */
  ocrLines?: readonly OcrLine[];
  company: CompanyInfo | null;
  categories: readonly CategoryOption[];
  instructions?: string | null;
  correction?: CorrectionContext | null;
  /** Production's compact instructions for the `header` and `items` asks (`baseSystemPrompt`). */
  compact?: boolean;
  /** From the transport. */
  inputBudget: number;
}

export interface BuiltPrompt {
  system: string;
  userPrompt: string;
  schema: Record<string, unknown>;
  image: ProviderImage | null;
  /** Blocks left out to fit the budget. */
  dropped: BlockName[];
}

function assemble(input: ReadPromptInput, kept: readonly BlockName[]): BuiltPrompt {
  const files = input.asks.some((name) => ASKS[name].files);
  const blocks = kept.map((name) => BLOCKS[name]);
  const image = blocks.some((block) => block.image) ? input.image : null;
  const context: BlockContext = {
    asks: input.asks,
    image: input.image,
    seesImage: image !== null,
    text: input.text,
    lines: input.lines,
    ocrLines: input.ocrLines ?? [],
    company: input.company,
    categories: input.categories,
    files,
  };
  const system = [
    baseSystemPrompt(input.asks, input.instructions, Boolean(input.correction), context.seesImage, input.compact),
    ...blocks.map((block) => block.system?.(context) ?? null),
  ];
  const user = [baseUserPrompt(input.asks, input.correction, context.seesImage), ...blocks.map((block) => block.user?.(context) ?? null)];
  const withCategories = files && kept.includes('categories') && input.categories.length > 0;
  return {
    system: system.filter((part): part is string => Boolean(part)).join('\n\n'),
    userPrompt: user.filter((part): part is string => Boolean(part)).join('\n\n'),
    schema: schemaFor(input.asks, withCategories),
    image,
    dropped: input.blocks.filter((name) => !kept.includes(name)),
  };
}

/**
 * The prompt with as many blocks as fit the budget. The droppable blocks are
 * ranked (`Block.drop`, lowest first), and the combinations are tried so the
 * higher-ranked blocks are kept longest: with the category list (1) and the
 * OCR text (2), that is everything, then without the list, then without the
 * text, then without both - production's order.
 */
export function buildReadPrompt(input: ReadPromptInput): BuiltPrompt {
  const droppable = input.blocks
    .filter((name) => BLOCKS[name].drop !== undefined)
    .sort((a, b) => BLOCKS[a].drop! - BLOCKS[b].drop!);
  const fits = (prompt: BuiltPrompt) =>
    estimateInputTokens(prompt.system, prompt.userPrompt, prompt.image ? 1 : 0, prompt.schema) <= input.inputBudget;
  let last: BuiltPrompt | null = null;
  for (let mask = 0; mask < 2 ** droppable.length; mask += 1) {
    const dropped = new Set(droppable.filter((_, bit) => mask & (1 << bit)));
    last = assemble(input, input.blocks.filter((name) => !dropped.has(name)));
    if (fits(last)) return last;
  }
  return last ?? assemble(input, input.blocks);
}

export interface CategorizePromptInput {
  merchant: string | null;
  lines: readonly CategoryLine[];
  company: CompanyInfo | null;
  categories: readonly CategoryOption[];
  useCompany: boolean;
  /** The receipt's whole OCR text, as context for the lines; `null` for none. */
  ocrText?: string | null;
  /** Whether the photo is sent along, as context for the lines. */
  seesImage?: boolean;
  /** What each main category holds (`describedCategories`); empty for nothing. */
  notes?: string;
  /** The compact answer (`compactCategorySchema`) instead of the full one. */
  compact?: boolean;
}

/** Upper bound on the OCR text a filing call is given, as a reading call has (`buildOcrContext`). */
const MAX_CATEGORIZE_OCR_CHARS = 8000;

/**
 * The filing call: the store, the numbered lines and the list (production's
 * follow-up), text only unless told otherwise. The receipt itself - its OCR
 * text, its photo - can go along as context: a section heading, an
 * abbreviation's neighbours or the look of the shop's print can say what a
 * line is where its name alone does not. The lines stay what is filed.
 */
export function buildCategorizePrompt(input: CategorizePromptInput): { system: string; userPrompt: string; schema: Record<string, unknown> } {
  let userPrompt = buildCategoryUserPrompt(input.merchant, input.lines);
  if (input.useCompany && input.company) userPrompt = `${companyText(input.company)}\n\n${userPrompt}`;
  const text = input.ocrText?.trim().slice(0, MAX_CATEGORIZE_OCR_CHARS);
  if (text) {
    userPrompt +=
      '\n\nFor context, this is the whole receipt as on-device OCR read it. Section headings, the lines around a ' +
      'product and the store\'s own abbreviations can tell what a line is. File only the numbered lines above; ' +
      `the OCR text makes mistakes and is not a list to file.\n\n<ocr>\n${text}\n</ocr>`;
  }
  if (input.seesImage) {
    userPrompt += '\n\nThe photo of the receipt is attached for context. File only the numbered lines above.';
  }
  if (input.compact) {
    const schema = compactCategorySchema(input.categories, input.lines.map((line) => line.line));
    return { system: buildCompactCategorySystemPrompt(input.categories, input.notes), userPrompt, schema };
  }
  return { system: buildCategorySystemPrompt(input.categories, input.notes), userPrompt, schema: CATEGORY_JSON_SCHEMA };
}
